import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { FaSearch, FaTimes, FaDownload, FaPlus, FaEdit, FaClone, FaTrashAlt, FaLayerGroup, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { Pagination, Button, TwIconButton, Badge, Confirmation } from 'components/common';
import {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  exportExpenses,
  deleteExpense,
} from 'redux/booking/travel/setting/expenseSlice';
import { debounce } from 'helpers/debounce';
import ExpenseForm from '../ExpenseForm';
import CloneExpenseConfirm from '../CloneExpenseConfirm';

const SEARCH_KEY = 'name_cont';

// One card per Application scope; each card lists the policies under that scope
// with per-row Edit / Clone / Delete — grouped for easy reading + manipulation.
const ExpensePolicyGrid = ({ stateKey, loadThunk, exportType }) => {
  const dispatch = useDispatch();
  const entry = useSelector((s) => s.booking_setting_expense[stateKey]) || {};
  const loading = useSelector((s) => s.booking_setting_expense.loading);
  const exporting = useSelector((s) => s.booking_setting_expense.exporting);

  const { data = [], totalRows = 0, totalPagesCount = 1, page = 1, pageSize = 50, currentParams = {} } = entry;

  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('New Expense policy');
  const [activeRecord, setActiveRecord] = useState(null);
  const [showClone, setShowClone] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) => setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const searchRef = useRef('');
  const perPageRef = useRef(pageSize);
  useEffect(() => { perPageRef.current = pageSize; }, [pageSize]);

  const runFetch = useCallback((pageNum) => {
    const params = { page: pageNum, per_page: perPageRef.current, ...(searchRef.current ? { [SEARCH_KEY]: searchRef.current } : {}) };
    dispatch(setNestedPage({ key: stateKey, page: pageNum }));
    dispatch(setNestedParams({ key: stateKey, params }));
    dispatch(loadThunk(params));
  }, [dispatch, stateKey, loadThunk]);

  const didInit = useRef(false);
  useEffect(() => {
    if (!didInit.current) { didInit.current = true; dispatch(loadThunk(currentParams)); }
  }, [dispatch, loadThunk, currentParams]);

  const debouncedSearch = useRef(debounce(() => runFetch(1), 500)).current;
  const handleSearch = (e) => { const v = e.target.value; setSearch(v); searchRef.current = v; debouncedSearch(); };
  const handleClearSearch = () => { setSearch(''); searchRef.current = ''; runFetch(1); };

  const handlePageChange = (p) => runFetch(p);
  const handlePageSizeChange = (sz) => { perPageRef.current = sz; dispatch(setNestedPageSize({ key: stateKey, pageSize: sz })); runFetch(1); };
  const handleExport = () => dispatch(exportExpenses({ ...currentParams, export_type: exportType }));

  const handleNew = () => { setActiveRecord(null); setFormTitle('New Expense policy'); setShowForm(true); };
  const handleEdit = (row) => { setActiveRecord(row); setFormTitle('Edit expense policy'); setShowForm(true); };
  const handleClone = (row) => { setActiveRecord(row); setShowClone(true); };
  const handleDelete = (row) => { setActiveRecord(row); setShowDelete(true); };

  // Group the current page of policies by Application scope (name).
  const groups = useMemo(() => {
    const map = new Map();
    data.forEach((row) => {
      const attrs = row.attributes || row;
      const key = attrs.name || 'Unspecified scope';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ id: row.id || attrs.id, ...attrs });
    });
    return Array.from(map.entries());
  }, [data]);

  const toggle = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div>
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-mb-3">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-64 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder="Application scope..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-solid tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700"
            />
            {search && (
              <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>

          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />

          <Button variant="primary" size="sm" leftIcon={FaPlus} onClick={handleNew}>New</Button>
          <Button variant="secondary" size="sm" leftIcon={FaDownload} loading={exporting} onClick={handleExport}>Export</Button>

          <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
            {(totalRows || 0).toLocaleString()} policies · {groups.length} scope(s)
          </span>
        </div>
      </div>

      <div className="tw-space-y-3">
        {loading && <div className="tw-text-center tw-text-slate-400 tw-text-sm tw-py-6">Loading…</div>}
        {!loading && groups.length === 0 && <div className="tw-text-center tw-text-slate-400 tw-text-sm tw-py-10">No expense policies</div>}

        {groups.map(([scope, rows]) => {
          const isCollapsed = collapsed[scope];
          return (
            <div key={scope} className="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(scope)}
                className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-bg-slate-50 tw-border-0 tw-border-b tw-border-solid tw-border-slate-200 tw-cursor-pointer tw-text-left"
              >
                {isCollapsed ? <FaChevronRight className="tw-text-slate-400" size={12} /> : <FaChevronDown className="tw-text-slate-400" size={12} />}
                <FaLayerGroup className="tw-text-blue-600" size={14} />
                <span className="tw-font-semibold tw-text-slate-800 tw-text-sm">{scope}</span>
                <Badge variant="primary" size="sm">{rows.length}</Badge>
              </button>

              {!isCollapsed && (
                <div className="tw-overflow-x-auto">
                  <table className="tw-w-full tw-border-collapse tw-text-sm">
                    <thead>
                      <tr className="tw-text-left tw-text-slate-500 tw-bg-white">
                        {['', 'Area', 'Expense type', 'Staff policy', 'Unit', 'Country', 'Position groups', ''].map((h, i) => (
                          <th key={i} className="tw-px-3 tw-py-2 tw-font-semibold tw-text-xs tw-uppercase tw-tracking-wide tw-border-b tw-border-solid tw-border-slate-100">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const open = expandedRows[row.id];
                        const rateList = row.rates || [];
                        return (
                          <React.Fragment key={row.id}>
                            <tr className="tw-border-b tw-border-solid tw-border-slate-100 hover:tw-bg-slate-50">
                              <td className="tw-px-3 tw-py-2">
                                <button type="button" onClick={() => toggleRow(row.id)} className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-sm tw-border-0 tw-bg-transparent tw-cursor-pointer tw-text-slate-400 hover:tw-text-blue-600">
                                  {open ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
                                </button>
                              </td>
                              <td className="tw-px-3 tw-py-2 tw-text-slate-700">{row.area}</td>
                              <td className="tw-px-3 tw-py-2 tw-text-slate-700">{row.expense_type}</td>
                              <td className="tw-px-3 tw-py-2"><Badge variant={row.staff_policy === 'CMD' ? 'warning' : 'info'} size="sm">{row.staff_policy}</Badge></td>
                              <td className="tw-px-3 tw-py-2 tw-text-slate-600">{row.unit}</td>
                              <td className="tw-px-3 tw-py-2 tw-text-slate-600">{Array.isArray(row.country_scope_name) ? row.country_scope_name.join(', ') : row.country_scope_name}</td>
                              <td className="tw-px-3 tw-py-2 tw-text-slate-600">
                                <button type="button" onClick={() => toggleRow(row.id)} className="tw-text-blue-600 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 hover:tw-underline">{rateList.length} group(s)</button>
                              </td>
                              <td className="tw-px-3 tw-py-2">
                                <div className="tw-flex tw-items-center tw-justify-end tw-gap-1">
                                  <TwIconButton variant="secondary" icon={FaEdit} size="sm" title="Edit" onClick={() => handleEdit(row)} />
                                  <TwIconButton variant="secondary" icon={FaClone} size="sm" title="Clone" onClick={() => handleClone(row)} />
                                  <TwIconButton variant="danger" icon={FaTrashAlt} size="sm" title="Delete" onClick={() => handleDelete(row)} />
                                </div>
                              </td>
                            </tr>
                            {open && (
                              <tr className="tw-bg-slate-50">
                                <td />
                                <td colSpan={7} className="tw-px-3 tw-py-2">
                                  <table className="tw-w-full tw-border-collapse tw-text-xs">
                                    <thead>
                                      <tr className="tw-text-left tw-text-slate-400">
                                        <th className="tw-px-2 tw-py-1 tw-font-semibold">Position group</th>
                                        <th className="tw-px-2 tw-py-1 tw-font-semibold tw-text-right">Amount</th>
                                        <th className="tw-px-2 tw-py-1 tw-font-semibold">Remark</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {rateList.length === 0 && <tr><td colSpan={3} className="tw-px-2 tw-py-1 tw-text-slate-300">No position groups</td></tr>}
                                      {rateList.map((rt) => (
                                        <tr key={rt.id} className="tw-border-t tw-border-solid tw-border-slate-100">
                                          <td className="tw-px-2 tw-py-1 tw-text-slate-700 tw-font-medium">{rt.position_group_name || `#${rt.position_group_id}`}</td>
                                          <td className="tw-px-2 tw-py-1 tw-text-right tw-text-slate-700">{rt.amount} {row.unit}</td>
                                          <td className="tw-px-2 tw-py-1 tw-text-slate-500">{rt.remark || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="tw-mt-3 tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm">
        <Pagination
          currentPage={page}
          totalPages={totalPagesCount}
          totalResults={totalRows}
          perPage={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[50, 100, 200]}
        />
      </div>

      {showForm && <ExpenseForm showModal={showForm} setshowModal={setShowForm} title={formTitle} record={activeRecord} />}
      {showClone && <CloneExpenseConfirm showModal={showClone} setshowModal={setShowClone} record={activeRecord} />}

      <Confirmation
        show={showDelete}
        type="danger"
        title="Delete expense policy"
        message="Are you sure to delete this policy and its rates?"
        confirmText="Delete"
        onConfirm={() => { if (activeRecord) dispatch(deleteExpense(activeRecord.id)); setShowDelete(false); }}
        onCancel={() => setShowDelete(false)}
      />
    </div>
  );
};

ExpensePolicyGrid.propTypes = {
  stateKey: PropTypes.string.isRequired,
  loadThunk: PropTypes.func.isRequired,
  exportType: PropTypes.string,
};

export default ExpensePolicyGrid;
